import React, { useState } from 'react';
import './Elements.css';
import { FaHeading, FaParagraph, FaLink, FaImage, FaList, FaListUl, FaTable, FaYoutube, FaMapMarkerAlt, FaDatabase, FaAlignLeft, FaBars, FaShoppingCart, FaStore, FaTag, FaCreditCard, FaCode, FaCaretDown } from 'react-icons/fa';
import { BiBox, BiSquareRounded, BiText, BiGrid, BiCarousel, BiRectangle, BiLayer, BiSlider } from 'react-icons/bi';
import { MdInput, MdCheckBox, MdRadioButtonChecked, MdLabel, MdTextFields, MdSend } from 'react-icons/md';
import DragPreview from './DragPreview';

const Elements = ({ addNewElement }) => {
  const [draggedElement, setDraggedElement] = useState(null);

  const elementCategories = [
    {
      title: 'Templates',
      items: [
        { 
          name: 'Navbar', 
          type: 'navbar-template', 
          icon: <FaBars />, 
          description: 'Navigation bar template' 
        }
      ]
    },
    {
      title: 'Ecommerce',
      items: [
        { name: 'Cart', type: 'cart', icon: <FaShoppingCart />, description: 'Shopping cart component' },
        { name: 'Add to Cart', type: 'add-to-cart', icon: <FaStore />, description: 'Add to cart button component' },
      ]
    },
    {
      title: 'Basic Elements',
      items: [
        { name: 'Div', type: 'div', icon: <BiRectangle />, description: 'Basic container element' },
        { name: 'Section', type: 'section', icon: <BiLayer />, description: 'Semantic section container' },
        { name: 'Container', type: 'div', icon: <BiBox />, description: 'A container to group elements' }
      ]
    },
    {
      title: 'Headings',
      items: [
        { name: 'H1', type: 'h1', icon: <FaHeading />, description: 'Main heading' },
        { name: 'H2', type: 'h2', icon: <FaHeading />, description: 'Secondary heading' },
        { name: 'H3', type: 'h3', icon: <FaHeading />, description: 'Tertiary heading' },
        { name: 'H4', type: 'h4', icon: <FaHeading />, description: 'Level 4 heading' },
        { name: 'H5', type: 'h5', icon: <FaHeading />, description: 'Level 5 heading' },
        { name: 'H6', type: 'h6', icon: <FaHeading />, description: 'Level 6 heading' }
      ]
    },
    {
      title: 'Content',
      items: [
        { name: 'Button', type: 'button', icon: <BiSquareRounded />, description: 'Clickable button element' },
        { name: 'Paragraph', type: 'p', icon: <FaParagraph />, description: 'Paragraph of text' },
        { name: 'Text', type: 'text', icon: <BiText />, description: 'Inline text content' },
        { name: 'Rich Text', type: 'rich-text', icon: <FaAlignLeft />, description: 'Rich text content with formatting' },
        { name: 'Link', type: 'link', icon: <FaLink />, description: 'Hyperlink element' },
        { name: 'Link Block', type: 'link-block', icon: <FaLink />, description: 'Anchor link that can contain other elements' },
        { name: 'List', type: 'list', icon: <FaListUl />, description: 'Unordered list' },
      ]
    },
    {
      title: 'Media',
      items: [
        { name: 'Image', type: 'image', icon: <FaImage />, description: 'Image element' },
        { name: 'Video', type: 'video', icon: <FaYoutube />, description: 'Video player' },
        { name: 'Slider', type: 'slider', icon: <BiSlider />, description: 'Image slider with navigation' },
        { name: 'Map', type: 'map', icon: <FaMapMarkerAlt />, description: 'Embedded map' },
      ]
    },
    {
      title: 'Layout',
      items: [
        { name: 'Grid', type: 'grid', icon: <BiGrid />, description: 'Grid layout container' },
        { name: 'Tabs', type: 'tabs', icon: <BiLayer />, description: 'Tabbed content container' },
        { name: 'List', type: 'list', icon: <FaList />, description: 'Ordered list' },
        { name: 'Unordered List', type: 'unorderedList', icon: <FaListUl />, description: 'Bullet points list' },
        { name: 'Table', type: 'table', icon: <FaTable />, description: 'Data table' }
      ]
    },
    {
      title: 'Form Elements',
      items: [
        { name: 'Form Block', type: 'form-block', icon: <BiBox />, description: 'Form container with basic elements' },
        { name: 'Label', type: 'label', icon: <MdLabel />, description: 'Form label element' },
        { name: 'Input', type: 'input', icon: <MdInput />, description: 'Text input field' },
        { name: 'Text Area', type: 'textarea', icon: <MdTextFields />, description: 'Multi-line text input' },
        { name: 'Checkbox', type: 'checkbox', icon: <MdCheckBox />, description: 'Checkbox input' },
        { name: 'Radio', type: 'radio', icon: <MdRadioButtonChecked />, description: 'Radio button input' },
        { name: 'Submit Button', type: 'submit', icon: <MdSend />, description: 'Form submit button' },
        { name: 'Dropdown', type: 'dropdown', icon: <FaCaretDown />, description: 'Dropdown element' }
      ]
    },
    {
      title: 'Embed',
      items: [
        { name: 'Custom Code', type: 'embed', icon: <FaCode />, description: 'Embed custom HTML, JS, or iframe code' },
      ]
    },
    {
      title: 'CMS',
      items: [
        { 
          name: 'CMS Content', 
          type: 'cms-content', 
          icon: <FaDatabase />,
          description: 'Display dynamic content from CMS' 
        },
      ]
    }
  ];

  const handleDragStart = (e, elementType, icon) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: elementType,
      isNew: true
    }));
    e.dataTransfer.effectAllowed = 'copy';
    
    // Add dragging class to the element being dragged
    e.currentTarget.classList.add('dragging');
    
    // Show drag preview with a slight delay to ensure it's set after drag starts
    setTimeout(() => {
      setDraggedElement({ 
        type: elementType, 
        icon,
        x: e.clientX,
        y: e.clientY
      });
    }, 0);

    // Hide the browser's default drag image
    const emptyImg = document.createElement('img');
    emptyImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(emptyImg, 0, 0);
  };

  const handleDragEnd = (e) => {
    // Remove dragging class
    e.currentTarget.classList.remove('dragging');
    setDraggedElement(null);
  };

  return (
    <div className="elements-container">
      {draggedElement && (
        <DragPreview 
          type={draggedElement.type} 
          icon={draggedElement.icon}
        />
      )}
      
      <div className="elements-search">
        <input type="text" placeholder="Search elements..." />
      </div>
      
      {elementCategories.map((category, index) => (
        <div key={index} className="element-category">
          <h3 className="category-title">{category.title}</h3>
          <div className="elements-grid">
            {category.items.map((element, elementIndex) => (
              <div
                key={elementIndex}
                className="element-item"
                onClick={() => addNewElement(element.type)}
                draggable="true"
                onDragStart={(e) => handleDragStart(e, element.type, element.icon)}
                onDragEnd={handleDragEnd}
                data-tooltip={element.description}
              >
                <div className="element-icon">
                  {element.icon}
                </div>
                <span className="element-name">{element.name}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Elements; 